// PKG-MKT-TRANSACT-SEC-LANDING (SEC-MKT-01) — the Marketplace purchase token
// is an opaque bearer valid for 24 h, and `POST /api/marketplace/saas/resolve`
// is unauthenticated: whoever holds it can mint a fresh activation handle and
// bind someone else's paid subscription to their own account. It must never
// reach Sentry, PostHog, a log, or any storage.
//
// The landing already stripped it from the address bar, but only after the
// resolve call settled — by then `initSentry()` and `initBetaAnalytics()` have
// long since run (appBootstrap imports both before the React root mounts), so
// the automatic `$pageview` and every Sentry breadcrumb carried the token.
// This module closes that window the same way the forum ticket does: capture
// into module memory and rewrite the URL BEFORE `main.tsx` imports the app
// graph, so neither SDK ever observes the original query.
//
// This file deliberately owns `PURCHASE_TOKEN_PARAM` (marketplaceApi re-exports
// it) so `main.tsx` can scrub without pulling in the API client, and so the
// telemetry modules can import the redactor without importing fetch code.

import { ROUTES } from "@/routes";

/** Query parameter Microsoft Marketplace uses to hand over the purchase token. */
export const PURCHASE_TOKEN_PARAM = "token";

/** Replacement written into telemetry payloads; never the real value. */
export const REDACTED_TOKEN = "REDACTED";

const MAX_TOKEN_LENGTH = 4_096;
const DEL_CHARACTER = 0x7f;
const FIRST_PRINTABLE_CHARACTER = 0x20;

let capturedToken: string | null = null;

/** Control characters cannot appear in a Marketplace token and hint at tampering. */
function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < FIRST_PRINTABLE_CHARACTER || code === DEL_CHARACTER) return true;
  }
  return false;
}

function normalizeToken(value: string | null): string | null {
  if (value === null) return null;
  const token = value.trim();
  if (!token || token.length > MAX_TOKEN_LENGTH) return null;
  if (hasControlCharacter(token)) return null;
  return token;
}

/**
 * Lower-cased and without a trailing slash. React Router matches routes
 * case-insensitively, so `/Marketplace/Activate` renders the landing too —
 * an exact-case comparison here would skip the scrub while the page still
 * read the token, leaving it in the URL for session replay (which
 * sanitize_properties does not cover).
 */
function normalizePathname(pathname: string): string {
  return pathname.replace(/[/]+$/, "").toLowerCase() || "/";
}

function relativeUrl(url: URL): string {
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Capture the purchase token into module memory and remove it from the address
 * bar. Runs before Sentry/PostHog initialise (see `main.tsx`).
 *
 * Scoped to {@link ROUTES.marketplaceActivate} ONLY, with or without a trailing
 * slash. Unlike the forum ticket — which is scrubbed wherever it appears —
 * `token` is a parameter other routes own and use: `/team/join?token=` is a
 * live invite flow, and deleting its query here would break it. Off-route the
 * URL is left exactly as it was.
 */
export function captureAndScrubMarketplacePurchaseToken(
  location: Pick<Location, "href"> = window.location,
  history: Pick<History, "replaceState" | "state"> = window.history,
): string | null {
  let url: URL;
  try {
    url = new URL(location.href);
  } catch {
    return capturedToken;
  }

  if (normalizePathname(url.pathname) !== ROUTES.marketplaceActivate.toLowerCase()) {
    return capturedToken;
  }
  if (!url.searchParams.has(PURCHASE_TOKEN_PARAM)) return capturedToken;

  // A duplicated parameter is ambiguous about which value Microsoft sent, so it
  // is scrubbed and captured as nothing: the landing then shows "no encontramos
  // esta compra", which sends the buyer back to Azure Portal for a fresh token.
  const values = url.searchParams.getAll(PURCHASE_TOKEN_PARAM);
  const token = values.length === 1 ? normalizeToken(values[0]) : null;
  url.searchParams.delete(PURCHASE_TOKEN_PARAM);

  try {
    history.replaceState(history.state, "", relativeUrl(url));
  } catch {
    /* history unavailable — the capture below still keeps the value out of
       everything that reads the token from this module */
  }

  capturedToken = token;
  return capturedToken;
}

/**
 * In-memory only; deliberately no sessionStorage/localStorage fallback.
 *
 * Reading is deliberately NON-destructive, unlike the query parameter it
 * replaced. A single-use read looks tidier — it would stop a Back button from
 * re-resolving a spent token — but it breaks StrictMode: React remounts the
 * page in development, the second mount finds the value already taken, and the
 * landing renders "No encontramos esta compra" for a perfectly good purchase.
 * Re-entry is instead bounded by the caller: MarketplaceActivatePage resolves
 * at most once per token via its own ref, and a real second hand-off from
 * Azure Portal is a full document load that re-runs the capture anyway.
 */
export function getCapturedMarketplacePurchaseToken(): string | null {
  return capturedToken;
}

/**
 * Replace the value of any `token=` parameter in a URL-ish string. Applied to
 * telemetry payloads as defence in depth, for any path the capture above does
 * not cover.
 *
 * Deliberately route-agnostic: the `/team/join` invite token is a bearer too,
 * and redacting it in telemetry costs nothing. This never touches the address
 * bar — only the copy an SDK is about to transmit.
 */
export function redactPurchaseTokenInUrl(value: string): string {
  // The regex below is case-insensitive; this fast path must be too, or an
  // uppercase parameter would short-circuit out unredacted.
  if (typeof value !== "string" || !value.toLowerCase().includes(`${PURCHASE_TOKEN_PARAM}=`)) {
    return value;
  }
  return value.replace(
    new RegExp(`([?&#]${PURCHASE_TOKEN_PARAM}=)[^&#]*`, "gi"),
    `$1${REDACTED_TOKEN}`,
  );
}

/** Test-only reset; production never clears the captured value. */
export function __resetMarketplacePurchaseTokenForTests(): void {
  capturedToken = null;
}
